import { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";

/** Una foto de evidencia (URL + fecha de subida para la trazabilidad). */
export type FotoEvidencia = {
  url: string;
  fecha?: string;
};

type Props = {
  label: string;
  value?: FotoEvidencia | null;
  /** Sube el archivo y devuelve la foto (URL). Si no viene, el campo es read-only. */
  onUpload?: (file: File) => Promise<FotoEvidencia>;
  onChange?: (foto: FotoEvidencia | null) => void;
  readOnly?: boolean;
};

/**
 * Campo compacto de una foto de evidencia: un BOTÓN para subir y, cuando ya hay
 * foto, un botón tipo "ojo" que abre la vista previa en un modal (no muestra la
 * miniatura inline, para no recargar la UI). En readOnly solo permite ver.
 */
export default function CampoFotoEvidencia({
  label,
  value,
  onUpload,
  onChange,
  readOnly = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [preview, setPreview] = useState(false);
  const editable = !readOnly && !!onUpload;
  const tieneFoto = !!value?.url;

  const pick = () => inputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-subir el mismo archivo
    if (!file || !onUpload) return;
    setSubiendo(true);
    try {
      const foto = await onUpload(file);
      onChange && onChange(foto);
    } catch (err) {
      console.error("No se pudo subir la evidencia", err);
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        px: 1,
        py: 0.5,
        width: "100%",
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: tieneFoto ? "success.light" : "divider",
        bgcolor: tieneFoto ? "rgba(46,125,50,0.04)" : "transparent",
        minWidth: 0,
      }}
    >
      {tieneFoto ? (
        <CheckCircleIcon sx={{ fontSize: 18, color: "success.main", flexShrink: 0 }} />
      ) : (
        <PhotoCameraOutlinedIcon sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0 }} />
      )}
      <Typography variant="caption" fontWeight={600} noWrap sx={{ minWidth: 0, flexGrow: 1 }}>
        {label}
      </Typography>

      {tieneFoto && (
        <Tooltip title="Ver evidencia">
          <IconButton size="small" color="primary" onClick={() => setPreview(true)}>
            <VisibilityOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {editable ? (
        <Button
          size="small"
          variant={tieneFoto ? "text" : "outlined"}
          startIcon={subiendo ? <CircularProgress size={14} /> : <PhotoCameraOutlinedIcon />}
          onClick={pick}
          disabled={subiendo}
          sx={{ flexShrink: 0 }}
        >
          {tieneFoto ? "Cambiar" : "Subir"}
        </Button>
      ) : !tieneFoto ? (
        <Chip size="small" label="Sin foto" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
      ) : null}

      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} />

      {/* Vista previa en modal */}
      <Dialog open={preview} onClose={() => setPreview(false)} maxWidth="md">
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            size="small"
            onClick={() => setPreview(false)}
            sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(0,0,0,0.4)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.6)" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {value?.url && (
            <img
              src={value.url}
              alt={label}
              style={{ display: "block", maxWidth: "90vw", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
          <Box sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {label}
            </Typography>
            {value?.fecha && (
              <Typography variant="caption" color="text.secondary">
                Subida: {dayjs(value.fecha).format("DD/MM/YYYY HH:mm")}
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
